/**
 * Purpose:
 *   User avatar component with dropdown menu for profile and session actions.
 *   Displays user initials in a circular badge with optional dropdown.
 *
 * Notes:
 *   - Shows user name on desktop, hides on mobile
 *   - User-facing labels are supplied by the parent for i18n compliance
 *   - Uses useAvatarDropdown hook for state management
 *   - Keyboard accessible with Escape to close
 */

'use client';

import React from 'react';
import { cx } from '@/util/functions/cx';
import { Icon } from '../icon/Icon';
import { useAvatarDropdown } from './(hooks)/useAvatarDropdown';
import {
  AVATAR_CONTAINER,
  AVATAR_BUTTON,
  AVATAR_NAME,
  AVATAR_CIRCLE,
  DROPDOWN_CONTAINER,
  DROPDOWN_OPEN,
  DROPDOWN_CLOSED,
  DROPDOWN_ITEM,
  DROPDOWN_DIVIDER,
  ICON_NEUTRAL,
} from './Avatar.styles';

export interface IAvatarProps {
  /** User display name */
  name: string;
  /** Avatar size in pixels */
  size?: number;
  /** Callback when profile is triggered */
  onProfile?: () => void;
  /** Callback when logout is triggered */
  onLogout?: () => void;
  /** Whether logout is in progress */
  isLoggingOut?: boolean;
  /** Accessible label for the dropdown menu */
  menuAriaLabel: string;
  /** Label for the profile menu item */
  profileLabel: string;
  /** Label for the logout menu item */
  logoutLabel: string;
  /** Label for the logout loading state */
  loggingOutLabel: string;
}

/**
 * Extracts initials from a name string.
 * For two or more words, uses first letter of first two words.
 * For single word, uses first two characters.
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0]?.slice(0, 2).toUpperCase() ?? '';
}

export const Avatar: React.FC<IAvatarProps> = ({
  name,
  size = 36,
  onProfile,
  onLogout,
  isLoggingOut = false,
  menuAriaLabel,
  profileLabel,
  logoutLabel,
  loggingOutLabel,
}) => {
  const { isOpen, dropdownRef, toggleDropdown, closeDropdown, dropdownState } =
    useAvatarDropdown();

  const initials = getInitials(name);

  const handleLogout = () => {
    closeDropdown();
    onLogout?.();
  };

  const handleProfile = () => {
    closeDropdown();
    onProfile?.();
  };

  return (
    <div ref={dropdownRef} className={AVATAR_CONTAINER}>
      {/* Avatar button */}
      <button
        type="button"
        onClick={toggleDropdown}
        className={AVATAR_BUTTON}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <span className={AVATAR_NAME}>{name}</span>
        <div
          className={AVATAR_CIRCLE}
          style={{ width: size, height: size }}
          aria-hidden="true"
        >
          {initials}
        </div>
      </button>

      {/* Dropdown menu */}
      <div
        className={cx(
          DROPDOWN_CONTAINER,
          dropdownState === 'open' ? DROPDOWN_OPEN : DROPDOWN_CLOSED
        )}
        role="menu"
        aria-label={menuAriaLabel}
      >
        {onProfile ? (
          <>
            <button
              type="button"
              onClick={handleProfile}
              className={DROPDOWN_ITEM}
              role="menuitem"
            >
              <Icon
                src="/icons/navigation/profile-edit.svg"
                size={16}
                className={ICON_NEUTRAL}
              />
              {profileLabel}
            </button>
            <div className={DROPDOWN_DIVIDER} aria-hidden="true" />
          </>
        ) : null}
        <button
          type="button"
          onClick={handleLogout}
          disabled={isLoggingOut}
          className={DROPDOWN_ITEM}
          role="menuitem"
        >
          <Icon src="/icons/logout.svg" size={16} className={ICON_NEUTRAL} />
          {isLoggingOut ? loggingOutLabel : logoutLabel}
        </button>
      </div>
    </div>
  );
};
